import type { FC, ReactNode } from 'react';
import { Box, CardActionArea, Stack, Typography } from '@mui/material';

interface Props {
  title: string;
  subtitle?: string;
  value: number;
  icon: ReactNode;
  color: string;
  onClick?: () => void;
  dataCy?: string;
  // False for every item except the leftmost, which drops the divider border.
  first?: boolean;
}

// Flat stat item for the dashboard top bar: coloured icon + count + label,
// divided from its neighbour by a thin left border. No icon circle, no progress
// ring — the height-efficient bar design from ENG-2280.
export const DashboardStatCard: FC<Props> = ({
  title,
  subtitle,
  value,
  icon,
  color,
  onClick,
  dataCy,
  first,
}) => {
  return (
    <CardActionArea
      data-cy={dataCy}
      onClick={onClick}
      disabled={!onClick}
      sx={{
        flex: '1 1 200px',
        minWidth: 0,
        borderRadius: 0,
        borderLeft: first ? 'none' : '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          py: 3,
        }}
      >
        <Box sx={{ color, display: 'flex', flexShrink: 0, fontSize: '24px' }}>
          {icon}
        </Box>
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="h4" sx={{ lineHeight: 1, fontWeight: 700 }}>
            {value}
          </Typography>
          <Typography variant="subtitle2" sx={{ mt: 0.5, fontWeight: 700 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.secondary',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: 1,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Stack>
      </Stack>
    </CardActionArea>
  );
};
